import { createApiRef, DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  BusinessUnit,
  TenantInfo,
  BuCatalogData,
  TenantCatalogData,
  PrSummary,
  RunStatus,
  TenantCosts,
  BuCosts,
  NonTenantProjectsData,
} from '../types';

export interface FoundationApi {
  getBusinessUnits(): Promise<BusinessUnit[]>;
  getTenants(buRepo: string): Promise<TenantInfo[]>;
  getBuCatalog(buRepo: string): Promise<BuCatalogData>;
  getTenantCatalog(tenantName: string, buRepo?: string): Promise<TenantCatalogData>;
  getBuPrs(buRepo: string): Promise<PrSummary[]>;
  getTenantPrs(tenantRepo: string): Promise<PrSummary[]>;
  getBuRuns(buRepo: string, workspaceId: string): Promise<RunStatus>;
  getTenantEnvRuns(
    tenantName: string,
    env: string,
    workspaceId: string,
  ): Promise<RunStatus>;
  getTenantCosts(
    tenantName: string,
    projectIds: string[],
    gcpAccessToken: string,
  ): Promise<TenantCosts>;
  getBuCosts(
    buRepo: string,
    tenantProjectMap: Record<string, string[]>,
    gcpAccessToken: string,
  ): Promise<BuCosts>;
  getNonTenantProjects(
    buRepo: string,
    gcpAccessToken: string,
    includeCosts?: boolean,
  ): Promise<NonTenantProjectsData>;
}

export const foundationApiRef = createApiRef<FoundationApi>({
  id: 'plugin.foundation.api',
});

export class FoundationClient implements FoundationApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('foundation');
  }

  private async get<T>(path: string, headers?: Record<string, string>): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(
      `${baseUrl}${path}`,
      headers ? { headers } : undefined,
    );
    if (!response.ok) {
      throw new Error(`Foundation API error: ${response.status} ${response.statusText}`);
    }
    return response.json();
  }

  async getBusinessUnits(): Promise<BusinessUnit[]> {
    return this.get('/business-units');
  }

  async getTenants(buRepo: string): Promise<TenantInfo[]> {
    return this.get(`/business-units/${encodeURIComponent(buRepo)}/tenants`);
  }

  async getBuCatalog(buRepo: string): Promise<BuCatalogData> {
    return this.get(`/business-units/${encodeURIComponent(buRepo)}/catalog`);
  }

  async getTenantCatalog(tenantName: string, buRepo?: string): Promise<TenantCatalogData> {
    const qs = buRepo ? `?buRepo=${encodeURIComponent(buRepo)}` : '';
    return this.get(`/tenants/${encodeURIComponent(tenantName)}/catalog${qs}`);
  }

  async getBuPrs(buRepo: string): Promise<PrSummary[]> {
    return this.get(`/business-units/${encodeURIComponent(buRepo)}/prs`);
  }

  async getTenantPrs(tenantRepo: string): Promise<PrSummary[]> {
    return this.get(`/tenants/${encodeURIComponent(tenantRepo)}/prs`);
  }

  async getBuRuns(buRepo: string, workspaceId: string): Promise<RunStatus> {
    return this.get(
      `/business-units/${encodeURIComponent(buRepo)}/runs?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
  }

  async getTenantEnvRuns(
    tenantName: string,
    env: string,
    workspaceId: string,
  ): Promise<RunStatus> {
    return this.get(
      `/tenants/${encodeURIComponent(tenantName)}/environments/${encodeURIComponent(env)}/runs?workspaceId=${encodeURIComponent(workspaceId)}`,
    );
  }

  async getTenantCosts(
    tenantName: string,
    projectIds: string[],
    gcpAccessToken: string,
  ): Promise<TenantCosts> {
    return this.get(
      `/tenants/${encodeURIComponent(tenantName)}/costs?projectIds=${projectIds.join(',')}`,
      { 'X-Gcp-Access-Token': gcpAccessToken },
    );
  }

  async getBuCosts(
    buRepo: string,
    tenantProjectMap: Record<string, string[]>,
    gcpAccessToken: string,
  ): Promise<BuCosts> {
    return this.get(
      `/business-units/${encodeURIComponent(buRepo)}/costs?tenantProjectMap=${encodeURIComponent(JSON.stringify(tenantProjectMap))}`,
      { 'X-Gcp-Access-Token': gcpAccessToken },
    );
  }

  async getNonTenantProjects(
    buRepo: string,
    gcpAccessToken: string,
    includeCosts = true,
  ): Promise<NonTenantProjectsData> {
    return this.get(
      `/business-units/${encodeURIComponent(buRepo)}/non-tenant-projects?includeCosts=${includeCosts}`,
      { 'X-Gcp-Access-Token': gcpAccessToken },
    );
  }
}
